// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { Coverage, Encounter, MedicationRequest, Patient, Practitioner } from '@medplum/fhirtypes';
import gelasioItalicUrl from '../assets/fonts/Gelasio-Italic.ttf?url';
import gelasioRegularUrl from '../assets/fonts/Gelasio-Regular.ttf?url';
import robotoBoldUrl from '../assets/fonts/Roboto-Bold.ttf?url';
import robotoMonoMediumUrl from '../assets/fonts/RobotoMono-Medium.ttf?url';
import { fetchPrimaryCoverage, fetchSecondaryCoverage, resolvePayorOrganization } from './patient-coverage';

export const RPM_ORDER_SYSTEM = 'https://polarissleep.com/fhir/prescription-type';
export const RPM_ORDER_DOCUMENT_TITLE = 'Remote Patient Monitoring Order';

export const RPM_MONITORING_PARAMETERS = [
  ['usage', 'Daily PAP usage (hrs/night)'],
  ['rahi', 'Residual AHI'],
  ['leak', 'Mask leak data'],
  ['pressure', 'PAP pressure (min/max/median)'],
  ['spo2', 'Pulse oximetry (SpO₂)'],
] as const;

export const RPM_ALERT_THRESHOLDS = [
  ['usage', 'PAP usage < 4 hrs/night for ≥ 3 consecutive nights'],
  ['rahi', 'Residual AHI > 10 events/hour'],
  ['leak', 'Mask leak exceeding manufacturer threshold'],
  ['spo2', 'SpO₂ < 88% (if monitored)'],
  ['symptoms', 'Patient-reported symptoms (daytime sleepiness, mask discomfort)'],
] as const;

const POLARIS_SLEEP_PHONE = '(301) 246-2152';
const MAROON = '#640040';
const INK = '#241d22';
const MUTED = '#6b6169';
const LINE = '#e6e1e4';
const TINT = '#f7f2f5';
/** 0.72in, in points. */
const PAGE_MARGIN = 51.84;
const CONTENT_WIDTH = 8.5 * 72 - 2 * PAGE_MARGIN;
/** Placeholder note values the order form writes when a field was left blank. */
const UNSET_NOTE_VALUES = new Set(['None', 'Not available', 'Not documented']);

/** The design is specified in CSS pixels; pdfmake lays out in points. */
function px(value: number): number {
  return value * 0.75;
}

/**
 * Converts a CSS line-height to pdfmake's, which multiplies the font's own line gap rather than the font size.
 * Roboto's ascender + descender span 1.172em.
 */
function lineHeight(css: number): number {
  return css / 1.172;
}

/**
 * Font families the design uses beyond pdfmake's bundled Roboto: Roboto Bold (700) for headings, Roboto Medium (500)
 * for field values, Roboto Mono for codes and IDs, and a Georgia-metric serif for the signature. Georgia itself cannot
 * be redistributed, so Gelasio — its metric-compatible open-licensed counterpart — stands in for it.
 */
const RPM_ORDER_PDF_FONT_FILES = {
  'Roboto-Bold.ttf': robotoBoldUrl,
  'RobotoMono-Medium.ttf': robotoMonoMediumUrl,
  'Gelasio-Regular.ttf': gelasioRegularUrl,
  'Gelasio-Italic.ttf': gelasioItalicUrl,
};

export const RPM_ORDER_PDF_FONTS = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Bold.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
  RobotoMedium: {
    normal: 'Roboto-Medium.ttf',
    bold: 'Roboto-Bold.ttf',
    italics: 'Roboto-MediumItalic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
  RobotoMono: {
    normal: 'RobotoMono-Medium.ttf',
    bold: 'RobotoMono-Medium.ttf',
    italics: 'RobotoMono-Medium.ttf',
    bolditalics: 'RobotoMono-Medium.ttf',
  },
  Gelasio: {
    normal: 'Gelasio-Regular.ttf',
    bold: 'Gelasio-Regular.ttf',
    italics: 'Gelasio-Italic.ttf',
    bolditalics: 'Gelasio-Italic.ttf',
  },
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
}

/** Fetches the extra font files as pdfmake virtual-file-system entries. Loaded on demand to keep them out of the bundle. */
export async function loadRpmOrderPdfFontFiles(): Promise<Record<string, string>> {
  const entries = await Promise.all(
    Object.entries(RPM_ORDER_PDF_FONT_FILES).map(async ([filename, url]): Promise<[string, string]> => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Unable to load PDF font ${filename}`);
      }
      return [filename, arrayBufferToBase64(await response.arrayBuffer())];
    })
  );
  return Object.fromEntries(entries);
}

export interface RpmOrderInsurance {
  payer?: string;
  memberId?: string;
  group?: string;
  secondaryCoverage?: string;
}

export interface RpmOrderPdfInput {
  patient: Patient;
  practitioner: Practitioner;
  request: MedicationRequest;
  /** When the ordering provider signed the order (the signature Provenance's `recorded`). */
  signedAt: string;
  insurance?: RpmOrderInsurance;
  /** The encounter the order was created from; absent for orders created standalone from the chart. */
  encounter?: Encounter;
}

export function isRpmOrderRequest(request: MedicationRequest): boolean {
  return (
    request.medicationCodeableConcept?.coding?.some(
      (coding) => coding.system === RPM_ORDER_SYSTEM && coding.code === 'rpm'
    ) ?? false
  );
}

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function noteValue(request: MedicationRequest, label: string): string | undefined {
  const prefix = `${label}:`;
  const raw = request.note
    ?.flatMap((note) => note.text?.split('\n') ?? [])
    .find((text) => text.startsWith(prefix))
    ?.slice(prefix.length)
    .trim();
  return raw && !UNSET_NOTE_VALUES.has(raw) ? raw : undefined;
}

function personName(resource: Patient | Practitioner): string {
  const current = resource.name?.[0];
  const base = [...((current?.given ?? []) as string[]), current?.family].filter(Boolean).join(' ');
  const suffix = current?.suffix?.join(', ');
  return nonEmpty(suffix && base ? `${base}, ${suffix}` : base) ?? '';
}

function formatLongDate(date: string | undefined): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date ?? '');
  if (!match) {
    return nonEmpty(date);
  }
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShortDate(date: string | undefined): string | undefined {
  if (!date) {
    return undefined;
  }
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatSignedAt(signedAt: string): string {
  const signed = new Date(signedAt);
  const date = signed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const time = signed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'shortGeneric' });
  return `${date} at ${time}`;
}

interface RpmDiagnosis {
  code: string;
  description: string;
}

/** Splits the order form's `Description (ICD-10)` diagnosis value into its code and description. */
export function parseRpmDiagnosis(value: string | undefined): RpmDiagnosis | undefined {
  const trimmed = nonEmpty(value);
  if (!trimmed) {
    return undefined;
  }
  const match = /^(.*\S)\s*\(([A-Z]\d[0-9A-Z.]*)\)$/.exec(trimmed);
  return match ? { description: match[1], code: match[2] } : { description: trimmed, code: '' };
}

interface RpmPapDevice {
  manufacturer?: string;
  model?: string;
  mode?: string;
  pressure?: string;
}

function papDevice(request: MedicationRequest): RpmPapDevice {
  const structured: RpmPapDevice = {
    manufacturer: noteValue(request, 'Device Manufacturer'),
    model: noteValue(request, 'Device Model'),
    mode: noteValue(request, 'Device Mode'),
    pressure: noteValue(request, 'Prescribed Pressure'),
  };
  if (Object.values(structured).some(Boolean)) {
    return structured;
  }
  // Orders saved before the device fields were stored separately only carry the combined `PAP Device` line, whose
  // empty parts were dropped; its parts are only positional when all four are present.
  const parts = noteValue(request, 'PAP Device')?.split(' · ') ?? [];
  if (parts.length === 4) {
    const [manufacturer, model, mode, pressure] = parts;
    return { manufacturer, model, mode, pressure };
  }
  return { manufacturer: parts.join(' · ') || undefined };
}

function selectedLabels(
  request: MedicationRequest,
  label: string,
  options: readonly (readonly [string, string])[]
): Set<string> {
  // Labels can contain commas, so match each known option instead of splitting the stored list.
  const stored = noteValue(request, label) ?? '';
  return new Set(options.filter(([, optionLabel]) => stored.includes(optionLabel)).map(([key]) => key));
}

function sourceEncounter(encounter: Encounter | undefined): string | undefined {
  if (!encounter) {
    return undefined;
  }
  const type =
    encounter.type?.[0]?.text ??
    encounter.type?.[0]?.coding?.[0]?.display ??
    encounter.serviceType?.text ??
    encounter.serviceType?.coding?.[0]?.display ??
    'Encounter';
  const date = formatShortDate(encounter.period?.start);
  return date ? `${type} · ${date}` : type;
}

function bottomRuleLayout(paddingBottom: number): Record<string, unknown> {
  return {
    hLineWidth: (index: number, node: { table: { body: unknown[] } }) => (index === node.table.body.length ? px(1) : 0),
    vLineWidth: () => 0,
    hLineColor: () => LINE,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => paddingBottom,
  };
}

function topRuleLayout(): Record<string, unknown> {
  return {
    hLineWidth: (index: number) => (index === 0 ? px(1) : 0),
    vLineWidth: () => 0,
    hLineColor: () => INK,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => px(6),
    paddingBottom: () => 0,
  };
}

function field(
  label: string,
  value: string | undefined,
  options: { fallback?: string; mono?: boolean } = {}
): Record<string, unknown> {
  const text = nonEmpty(value);
  return {
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              { text: label.toUpperCase(), style: 'fieldLabel' },
              { text: text ?? options.fallback ?? '—', style: text && options.mono ? 'fieldMono' : 'fieldValue' },
            ],
          },
        ],
      ],
    },
    layout: bottomRuleLayout(px(4)),
  };
}

/** Lays fields out in rows of `perRow` equal columns, padding the last row so column widths stay aligned. */
function fieldGrid(fields: Record<string, unknown>[], perRow: number, columnGap: number): Record<string, unknown> {
  const rows: Record<string, unknown>[] = [];
  for (let index = 0; index < fields.length; index += perRow) {
    const row = fields.slice(index, index + perRow);
    while (row.length < perRow) {
      row.push({ text: '' });
    }
    rows.push({
      columns: row,
      columnGap: px(columnGap),
      margin: [0, 0, 0, index + perRow < fields.length ? px(4) : 0],
    });
  }
  return { stack: rows };
}

function block(body: Record<string, unknown>[], label?: string): Record<string, unknown> {
  return {
    unbreakable: true,
    stack: [...(label ? [{ text: label.toUpperCase(), style: 'sectionHeading' }] : []), ...body],
    margin: [0, 0, 0, px(4)],
  };
}

function checkbox(checked: boolean): Record<string, unknown> {
  // The design's box is an 11px content box inside a 1.5px border, which browsers render at 1px: 13px overall.
  const size = px(13);
  const border = px(1);
  return {
    width: size,
    margin: [0, px(2), 0, 0],
    canvas: checked
      ? [
          { type: 'rect', x: 0, y: 0, w: size, h: size, r: px(2), color: MAROON },
          {
            // The design's rotated-border tick, resolved to its three stroke points.
            type: 'polyline',
            points: [
              { x: px(2.46), y: px(5.21) },
              { x: px(4.94), y: px(7.68) },
              { x: px(9.54), y: px(3.09) },
            ],
            lineWidth: border,
            lineColor: '#ffffff',
          },
        ]
      : [
          {
            type: 'rect',
            x: border / 2,
            y: border / 2,
            w: size - border,
            h: size - border,
            r: px(2),
            lineWidth: border,
            lineColor: INK,
          },
        ],
  };
}

function checkRow(label: string, checked: boolean): Record<string, unknown> {
  return {
    columns: [checkbox(checked), { text: label, style: checked ? 'checkOn' : 'checkOff' }],
    columnGap: px(8),
  };
}

function checkList(
  options: readonly (readonly [string, string])[],
  selected: Set<string>,
  perRow: number
): Record<string, unknown> {
  const rows: Record<string, unknown>[] = [];
  for (let index = 0; index < options.length; index += perRow) {
    const row = options.slice(index, index + perRow).map(([key, label]) => checkRow(label, selected.has(key)));
    const margin = [0, 0, 0, index + perRow < options.length ? px(2) : 0];
    // A lone item on the last row spans the full width so a long label does not wrap.
    if (row.length === 1) {
      rows.push({ ...row[0], margin });
      continue;
    }
    while (row.length < perRow) {
      row.push({ text: '' });
    }
    rows.push({ columns: row, columnGap: px(20), margin });
  }
  return { stack: rows };
}

function diagnosisRow(diagnosis: RpmDiagnosis, tag: string): Record<string, unknown> {
  return {
    table: {
      widths: [px(70), '*', 'auto'],
      body: [
        [
          { text: diagnosis.code || '—', style: 'diagnosisCode' },
          { text: diagnosis.description, style: 'diagnosisText' },
          { text: tag.toUpperCase(), style: 'diagnosisTag' },
        ],
      ],
    },
    layout: { ...bottomRuleLayout(px(5)), paddingRight: (index: number) => (index < 2 ? px(14) : 0) },
    margin: [0, 0, 0, px(3)],
  };
}

/** A signature-block column: a top rule over its content, at least `minWidth` wide. */
function signatureColumn(
  width: number | 'auto',
  minWidth: number,
  body: Record<string, unknown>[]
): Record<string, unknown> {
  return {
    width,
    table: {
      widths: [width === 'auto' ? 'auto' : width],
      body: [
        [
          {
            stack: [
              // Zero-height spacer that holds the column open to its minimum width.
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: minWidth, y2: 0, lineWidth: 1, strokeOpacity: 0 }] },
              ...body,
            ],
          },
        ],
      ],
    },
    layout: topRuleLayout(),
  };
}

export function buildRpmOrderPdfDefinition(input: RpmOrderPdfInput, logoDataUri?: string): Record<string, unknown> {
  const { patient, practitioner, request, signedAt, insurance = {} } = input;
  const orderDate = formatLongDate(request.authoredOn ?? signedAt) ?? '';
  const practitionerNpi = practitioner.identifier?.find((entry) => entry.system?.toLowerCase().includes('npi'))?.value;
  const practitionerPhone = practitioner.telecom?.find((entry) => entry.system === 'phone')?.value;
  const primaryDiagnosis = parseRpmDiagnosis(noteValue(request, 'Diagnosis'));
  const secondaryDiagnosis = parseRpmDiagnosis(noteValue(request, 'Secondary Diagnosis'));
  const device = papDevice(request);
  const parameters = selectedLabels(request, 'Monitoring Parameters', RPM_MONITORING_PARAMETERS);
  const alerts = selectedLabels(request, 'Alert Thresholds', RPM_ALERT_THRESHOLDS);
  const providerName = personName(practitioner);
  const logoHeight = px(28);

  return {
    pageSize: 'LETTER',
    pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN],
    ...(logoDataUri ? { images: { polarisLogo: logoDataUri } } : {}),
    content: [
      {
        columns: [
          {
            width: '*',
            stack: [
              ...(logoDataUri
                ? [{ image: 'polarisLogo', width: (logoHeight * 294) / 56, height: logoHeight }]
                : [{ text: 'POLARIS', style: 'brand' }]),
              { text: 'SLEEP MEDICINE', style: 'brandSubtitle' },
            ],
          },
          {
            width: 'auto',
            text: `1032 15th St NW #418\nWashington, DC 20005\n${POLARIS_SLEEP_PHONE} · fax (260) 668-2001\nEIN 41-4815927`,
            alignment: 'right',
            style: 'clinicDetails',
          },
        ],
        columnGap: px(24),
        margin: [0, 0, 0, px(8)],
      },
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: px(2), lineColor: MAROON }],
        margin: [0, 0, 0, px(10)],
      },
      { text: RPM_ORDER_DOCUMENT_TITLE, style: 'documentTitle' },
      { text: `Obstructive Sleep Apnea — PAP Therapy Monitoring · ${orderDate}`, style: 'documentSubtitle' },
      block(
        [
          fieldGrid(
            [
              field('Name', personName(patient)),
              field('Date of birth', formatLongDate(patient.birthDate)),
              field('MRN', patient.identifier?.[0]?.value, { mono: true }),
              field('Phone', patient.telecom?.find((entry) => entry.system === 'phone')?.value),
              field('Order type', 'RPM enrollment'),
              field('Monitoring duration', noteValue(request, 'Monitoring Duration')),
              field('Order date', orderDate),
              field('Source encounter', sourceEncounter(input.encounter), { fallback: '' }),
            ],
            4,
            16
          ),
        ],
        'Patient and order'
      ),
      block([
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  text: [
                    { text: 'Enroll patient in Remote Patient Monitoring', bold: true },
                    ' for PAP therapy adherence and efficacy monitoring. Data transmitted via PAP device modem / cloud platform (AirView).',
                  ],
                  style: 'callout',
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: (index: number) => (index === 0 ? px(3) : 0),
            vLineColor: () => MAROON,
            fillColor: () => TINT,
            paddingLeft: () => px(13),
            paddingRight: () => px(13),
            paddingTop: () => px(9),
            paddingBottom: () => px(9),
          },
        },
      ]),
      block(
        [
          fieldGrid(
            [
              field('Insurance', insurance.payer, { fallback: 'None on file' }),
              field('Member ID', insurance.memberId, { mono: true }),
              field('Group', insurance.group, { mono: true }),
              field('Secondary coverage', insurance.secondaryCoverage, { fallback: 'None on file' }),
            ],
            4,
            16
          ),
        ],
        'Insurance'
      ),
      block(
        [
          ...(primaryDiagnosis ? [diagnosisRow(primaryDiagnosis, 'Primary')] : []),
          ...(secondaryDiagnosis ? [diagnosisRow(secondaryDiagnosis, 'Secondary')] : []),
          {
            ...fieldGrid(
              [
                field('OSA severity', noteValue(request, 'OSA Severity'), { fallback: 'Pending diagnostic study' }),
                field('AHI', noteValue(request, 'AHI from study results')),
                field('Study date', formatLongDate(noteValue(request, 'Date of Sleep Study'))),
              ],
              4,
              16
            ),
            margin: [0, px(1), 0, 0],
          },
        ],
        'Diagnosis'
      ),
      block(
        [
          fieldGrid(
            [
              field('Manufacturer', device.manufacturer),
              field('Model', device.model),
              field('Mode', device.mode),
              field('Prescribed pressure', device.pressure),
            ],
            4,
            16
          ),
        ],
        'PAP device'
      ),
      block([checkList(RPM_MONITORING_PARAMETERS, parameters, 3)], 'Monitoring parameters'),
      block([checkList(RPM_ALERT_THRESHOLDS, alerts, 2)], 'Alert thresholds'),
      block(
        [
          fieldGrid(
            [
              field('Ordering provider', providerName),
              field('NPI', practitionerNpi, { mono: true }),
              field('Practice', 'Polaris Sleep'),
              field('Contact', practitionerPhone ?? POLARIS_SLEEP_PHONE),
            ],
            4,
            16
          ),
        ],
        'Ordered by'
      ),
      {
        unbreakable: true,
        stack: [
          {
            text: "I certify that remote physiologic monitoring is medically necessary for this patient's PAP therapy management, that the patient has consented to enrollment, and that the information above is accurate.",
            style: 'attestation',
          },
          {
            columns: [
              signatureColumn(px(330), px(330), [
                { text: providerName, style: 'signatureName' },
                { text: `Electronically signed ${formatSignedAt(signedAt)}`, style: 'signatureMeta' },
                { text: `NPI ${practitionerNpi ?? '—'}`, style: 'signatureMeta' },
              ]),
              signatureColumn('auto', px(150), [
                { text: orderDate, style: 'signatureDate' },
                { text: 'Date of order', style: 'signatureMeta' },
              ]),
            ],
            columnGap: px(40),
            margin: [0, px(6), 0, 0],
          },
        ],
      },
      {
        unbreakable: true,
        stack: [
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: px(1), lineColor: LINE }],
            margin: [0, px(5), 0, px(5)],
          },
          {
            text: `Monitoring data is reviewed by the Polaris Sleep clinical team. Alerts route to the RPM triage queue. Questions: ${POLARIS_SLEEP_PHONE}.`,
            style: 'footer',
          },
        ],
      },
    ],
    styles: {
      brand: { fontSize: 20, bold: true, color: MAROON },
      brandSubtitle: { fontSize: 9, color: MUTED, characterSpacing: 0.81, margin: [0, px(5), 0, 0] },
      clinicDetails: { fontSize: 9, color: MUTED, lineHeight: lineHeight(1.5) },
      documentTitle: { fontSize: 14, bold: true, color: INK, margin: [0, 0, 0, px(3)] },
      documentSubtitle: { fontSize: 9, color: MUTED, margin: [0, 0, 0, px(6)] },
      sectionHeading: { fontSize: 8.5, bold: true, color: MAROON, characterSpacing: 0.85, margin: [0, 0, 0, px(5)] },
      fieldLabel: { fontSize: 8, color: MUTED, characterSpacing: 0.32, margin: [0, 0, 0, px(1)] },
      fieldValue: { font: 'RobotoMedium', fontSize: 10, color: INK },
      fieldMono: { font: 'RobotoMono', fontSize: 9.5, color: INK },
      callout: { fontSize: 9.5, color: INK, lineHeight: lineHeight(1.5) },
      diagnosisCode: { font: 'RobotoMono', fontSize: 10, color: MAROON },
      diagnosisText: { fontSize: 10, color: INK },
      diagnosisTag: { fontSize: 8.5, color: MUTED, characterSpacing: 0.34 },
      checkOn: { fontSize: 9.5, color: INK, lineHeight: lineHeight(1.4) },
      checkOff: { fontSize: 9.5, color: MUTED, lineHeight: lineHeight(1.4) },
      attestation: { fontSize: 9.5, color: INK, lineHeight: lineHeight(1.5), margin: [0, px(5), 0, 0] },
      signatureName: {
        font: 'Gelasio',
        fontSize: 17,
        italics: true,
        lineHeight: lineHeight(1.1),
        margin: [0, 0, 0, px(4)],
      },
      signatureDate: { font: 'Gelasio', fontSize: 17, lineHeight: lineHeight(1.1), margin: [0, 0, 0, px(4)] },
      signatureMeta: { fontSize: 8.5, color: MUTED, lineHeight: lineHeight(1.6) },
      footer: { fontSize: 8, color: MUTED, lineHeight: lineHeight(1.5) },
    },
    defaultStyle: { font: 'Roboto', fontSize: 10, color: INK, lineHeight: lineHeight(1.2) },
  };
}

function coverageClassValue(coverage: Coverage | undefined, type: string): string | undefined {
  return coverage?.class?.find((entry) => entry.type?.coding?.some((coding) => coding.code === type))?.value;
}

async function payerName(medplum: MedplumClient, coverage: Coverage): Promise<string | undefined> {
  return coverage.payor?.[0]?.display ?? (await resolvePayorOrganization(medplum, coverage))?.name;
}

/** Reads the chart data the RPM order form shows at render time: insurance and the source encounter. */
export async function fetchRpmOrderPdfContext(
  medplum: MedplumClient,
  patient: Patient,
  request: MedicationRequest
): Promise<Pick<RpmOrderPdfInput, 'insurance' | 'encounter'>> {
  const encounterPromise = request.encounter?.reference?.startsWith('Encounter/')
    ? medplum.readReference<Encounter>(request.encounter as { reference: string }).catch((): undefined => undefined)
    : Promise.resolve(undefined);
  const primary = patient.id ? await fetchPrimaryCoverage(medplum, patient.id).catch(() => undefined) : undefined;
  const secondary =
    patient.id && primary
      ? await fetchSecondaryCoverage(medplum, patient.id, primary.id).catch(() => undefined)
      : undefined;
  return {
    insurance: primary
      ? {
          payer: await payerName(medplum, primary),
          memberId: primary.subscriberId,
          group: coverageClassValue(primary, 'group'),
          secondaryCoverage: secondary ? await payerName(medplum, secondary) : undefined,
        }
      : {},
    encounter: await encounterPromise,
  };
}
